import { Plus, Edit2, Trash2, Copy, Users, User } from "lucide-react";
import React, { useState, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { Button } from "@/app/_module/components/ui/button";
import { useUserContext } from "@/app/_module/store/contexts/userContext/userContexts";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/app/_module/components/ui/card";
import {
  Dialog,
  ResponsiveDialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/app/_module/components/ui/dialog";
import { Input } from "@/app/_module/components/ui/input";
import { Label } from "@/app/_module/components/ui/label";
import { Textarea } from "@/app/_module/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/_module/components/ui/select";
import { Badge } from "@/app/_module/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  ResponsiveAlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/app/_module/components/ui/alert-dialog";

import { useGetSmsTemplates } from "../hooks/queries";
import { createSmsTemplate, updateSmsTemplate, deleteSmsTemplate } from "../services/sms-settings-service";
import type { SmsTemplate } from "../types/sms-settings";
import { smsSettingsQueryKeys } from "../services/sms-settings-keys";

const TEMPLATE_CATEGORIES = [
  { value: "appointment", label: "Appointment", color: "bg-blue-100 text-blue-800" },
  { value: "follow-up", label: "Follow-up", color: "bg-green-100 text-green-800" },
  { value: "quote", label: "Quote", color: "bg-purple-100 text-purple-800" },
  { value: "thank-you", label: "Thank You", color: "bg-orange-100 text-orange-800" },
  { value: "reminder", label: "Reminder", color: "bg-yellow-100 text-yellow-800" },
  { value: "custom", label: "Custom", color: "bg-gray-100 text-gray-800" },
];

const VARIABLE_PLACEHOLDERS = [
  { name: "{{name}}", description: "Lead's full name", category: "Basic Info" },
  { name: "{{first_name}}", description: "Lead's first name", category: "Basic Info" },
  { name: "{{last_name}}", description: "Lead's last name", category: "Basic Info" },
  { name: "{{email}}", description: "Lead's email address", category: "Contact Info" },
  { name: "{{recipient_phone}}", description: "Lead's phone number", category: "Contact Info" },
  { name: "{{phone}}", description: "Your outbound phone (area code matched)", category: "Agent Info" },
  { name: "{{my_phone}}", description: "Your outbound phone (same as {{phone}})", category: "Agent Info" },
  { name: "{{agent_phone}}", description: "Your outbound phone (same as {{phone}})", category: "Agent Info" },
  { name: "{{agency_name}}", description: "Your agency name", category: "Agent Info" },
  { name: "{{address}}", description: "Lead's street address", category: "Address" },
  { name: "{{city}}", description: "Lead's city", category: "Address" },
  { name: "{{state}}", description: "Lead's state", category: "Address" },
  { name: "{{zip_code}}", description: "Lead's ZIP code", category: "Address" },
  { name: "{{age}}", description: "Lead's age", category: "Personal Details" },
  { name: "{{dob}}", description: "Lead's date of birth", category: "Personal Details" },
  { name: "{{gender}}", description: "Lead's gender", category: "Personal Details" },
  { name: "{{marital_status}}", description: "Lead's marital status", category: "Personal Details" },
];

export const SMSTemplateManagement = function() {
  const { data: templatesData, isLoading, error } = useGetSmsTemplates();
  const queryClient = useQueryClient();
  const { user } = useUserContext();

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<SmsTemplate | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const contentTextareaRef = useRef<HTMLTextAreaElement>(null);
  const editContentTextareaRef = useRef<HTMLTextAreaElement>(null);

  const [formData, setFormData] = useState<{
    name: string;
    content: string;
    category: string;
    scope?: 'personal' | 'agency';
  }>({
    name: "",
    content: "",
    category: "custom",
    scope: "personal", // Default to personal (only creator can see)
  });

  // handleApiCall extracts the 'data' field, so templatesData is already the pagination object
  const allTemplates = templatesData?.items || [];

  // Check if user is agency owner or admin
  const agencyRole = user?.user?.agencyRole || user?.currentAgency?.role;
  const isAgencyLeader = agencyRole === 'owner' || agencyRole === 'admin';
  const userId = user?.user?.id || user?.user?.userId;
  const agencyId = (user?.currentAgency as any)?.id || user?.user?.agencyId;

  // CRITICAL: Client-side filtering to ensure user only sees templates they should see
  // This is a safety net in case backend filtering doesn't work properly
  const templates = allTemplates.filter((template: SmsTemplate) => {
    // Allow global templates (both null or undefined) - these are system defaults
    // These are the 4 default templates that should be visible to everyone
    const isGlobal = (!template.agencyId || template.agencyId === null) &&
                     (!template.userId || template.userId === null);
    if (isGlobal) {
      return true;
    }

    // Agency-wide templates - ALL agents in the same agency can see and use them
    // Only owners/admins can edit/delete, but everyone can see and select them
    if (template.agencyId === agencyId && !template.userId) {
      return true; // Changed from isAgencyLeader to true - all agents can see
    }

    // Personal templates - only show if userId matches exactly
    if (template.userId === userId && template.agencyId === agencyId) {
      return true;
    }

    // Everything else should be filtered out (other users' templates, other agencies, etc.)
    return false;
  });

  const handleCreateTemplate = async () => {
    if (!formData.name || !formData.content) {
      return;
    }

    setIsSaving(true);
    try {
      // Ensure scope is always set - default to personal for non-agency leaders
      const templateData = {
        ...formData,
        scope: formData.scope || (isAgencyLeader ? 'agency' : 'personal'),
      };
      await createSmsTemplate(templateData);
      queryClient.invalidateQueries({ queryKey: smsSettingsQueryKeys.templates() });
      setIsCreateDialogOpen(false);
      setFormData({ name: "", content: "", category: "custom", scope: "personal" });
    } catch (error) {

    } finally {
      setIsSaving(false);
    }
  };

  const handleEditTemplate = async () => {
    if (!selectedTemplate || !formData.name || !formData.content) {
      return;
    }

    setIsSaving(true);
    try {
      // Ensure scope is always set when updating
      const templateData = {
        ...formData,
        scope: formData.scope || (isAgencyLeader ? 'agency' : 'personal'),
      };
      await updateSmsTemplate(selectedTemplate.id, templateData);
      queryClient.invalidateQueries({ queryKey: smsSettingsQueryKeys.templates() });
      setIsEditDialogOpen(false);
      setSelectedTemplate(null);
      setFormData({ name: "", content: "", category: "custom", scope: "personal" });
    } catch (error) {

    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteTemplate = async () => {
    if (!selectedTemplate) return;

    setIsSaving(true);
    try {
      await deleteSmsTemplate(selectedTemplate.id);
      queryClient.invalidateQueries({ queryKey: smsSettingsQueryKeys.templates() });
      setIsDeleteDialogOpen(false);
      setSelectedTemplate(null);
    } catch (error) {

    } finally {
      setIsSaving(false);
    }
  };

  const openEditDialog = (template: SmsTemplate) => {
    setSelectedTemplate(template);
    setFormData({
      name: template.name,
      content: template.content,
      category: template.category,
      scope: template.userId ? 'personal' : 'agency', // If userId is set, it's personal, otherwise agency-wide
    });
    setIsEditDialogOpen(true);
  };

  const openDeleteDialog = (template: SmsTemplate) => {
    setSelectedTemplate(template);
    setIsDeleteDialogOpen(true);
  };

  const insertPlaceholder = (placeholder: string, isEditMode = false) => {
    const textarea = isEditMode ? editContentTextareaRef.current : contentTextareaRef.current;

    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const currentContent = formData.content;

      // Insert at cursor position
      const newContent = currentContent.substring(0, start) + placeholder + currentContent.substring(end);

      setFormData(prev => ({
        ...prev,
        content: newContent,
      }));

      // Set cursor position after the inserted placeholder
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + placeholder.length, start + placeholder.length);
      }, 0);
    } else {
      // Fallback to appending if textarea ref is not available
      setFormData(prev => ({
        ...prev,
        content: prev.content + placeholder,
      }));
    }
  };

  const getCategoryColor = (category: string) => {
    const cat = TEMPLATE_CATEGORIES.find(c => c.value === category);
    return cat?.color || "bg-gray-100 text-gray-800";
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>SMS Template Management</CardTitle>
          <CardDescription>Loading templates...</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            <div className="h-20 bg-gray-200 rounded" />
            <div className="h-20 bg-gray-200 rounded" />
            <div className="h-20 bg-gray-200 rounded" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>SMS Template Management</CardTitle>
          <CardDescription className="text-red-600">
            Error loading templates
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-red-500">
            Failed to load SMS templates. Please try again.
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>SMS Template Management</CardTitle>
              <CardDescription>
                Create and manage your SMS templates for quick messaging
              </CardDescription>
            </div>
            <Button onClick={() => setIsCreateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              New Template
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {templates.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground mb-4">
                No templates yet. Create your first template to get started!
              </p>
              <Button onClick={() => setIsCreateDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Template
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {templates.map((template) => {
                // Check if this is a global template (cannot be edited/deleted)
                const isGlobalTemplate = !template.agencyId && !template.userId;
                const isPersonalTemplate = template.userId !== null && template.userId !== undefined;
                const isAgencyWideTemplate = template.agencyId === agencyId && !template.userId;

                // Only owners/admins can edit/delete agency-wide templates
                // Regular agents can only edit/delete their own personal templates
                const canEdit = isGlobalTemplate
                  ? false
                  : isAgencyWideTemplate
                    ? isAgencyLeader
                    : isPersonalTemplate && template.userId === userId;

                return (
                  <div
                    key={template.id}
                    className="border rounded-lg p-4 hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-lg">{template.name}</h3>
                          <Badge className={getCategoryColor(template.category)}>
                            {TEMPLATE_CATEGORIES.find(c => c.value === template.category)?.label || template.category}
                          </Badge>
                          {isGlobalTemplate && (
                            <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                              System Default
                            </Badge>
                          )}
                          {!isGlobalTemplate && (
                            <Badge
                              variant="outline"
                              className={isPersonalTemplate ? "bg-purple-50 text-purple-700 border-purple-200" : "bg-green-50 text-green-700 border-green-200"}
                            >
                              {isPersonalTemplate ? (
                                <><User className="h-3 w-3 mr-1" /> Personal</>
                              ) : (
                                <><Users className="h-3 w-3 mr-1" /> Agency-wide</>
                              )}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {template.content}
                        </p>
                      </div>
                      {canEdit && (
                        <div className="flex items-center gap-2 ml-4">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(template)}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openDeleteDialog(template)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Template Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <ResponsiveDialogContent size="2xl" className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create SMS Template</DialogTitle>
            <DialogDescription>
              Create a new reusable SMS template with customizable variables
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Template Name</Label>
              <Input
                id="name"
                placeholder="Appointment Confirmation"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {TEMPLATE_CATEGORIES.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {isAgencyLeader && (
              <div className="space-y-2">
                <Label htmlFor="scope">Template Scope</Label>
                <Select
                  value={formData.scope || "personal"}
                  onValueChange={(value: 'personal' | 'agency') => setFormData(prev => ({ ...prev, scope: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select scope" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="agency">
                      Agency-wide (All agents within the agency can use)
                    </SelectItem>
                    <SelectItem value="personal">
                      Personal (Only I can use)
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {formData.scope === 'agency'
                    ? "This template will be visible to all agents within your agency"
                    : "This template will only be visible to you"}
                </p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="content">Template Content</Label>
              <Textarea
                ref={contentTextareaRef}
                id="content"
                placeholder="Hi {{name}}, this is a reminder..."
                className="min-h-[120px]"
                value={formData.content}
                onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
              />
              <p className="text-xs text-muted-foreground">
                Character count: {formData.content.length} / 160 (1 SMS)
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="insert-variable">Insert Variable</Label>
              <Select
                value=""
                onValueChange={(value) => {
                  if (value) {
                    insertPlaceholder(value);
                  }
                }}
              >
                <SelectTrigger id="insert-variable">
                  <SelectValue placeholder="Select a variable to insert..." />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {Object.entries(
                    VARIABLE_PLACEHOLDERS.reduce((acc, placeholder) => {
                      if (!acc[placeholder.category]) {
                        acc[placeholder.category] = [];
                      }
                      acc[placeholder.category].push(placeholder);
                      return acc;
                    }, {} as Record<string, typeof VARIABLE_PLACEHOLDERS>)
                  ).map(([category, placeholders]) => (
                    <React.Fragment key={category}>
                      <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50">
                        {category}
                      </div>
                      {placeholders.map((placeholder) => (
                        <SelectItem key={placeholder.name} value={placeholder.name}>
                          <div className="flex items-center justify-between w-full gap-4">
                            <code className="font-mono text-xs font-semibold text-blue-600">
                              {placeholder.name}
                            </code>
                            <span className="text-xs text-muted-foreground">
                              {placeholder.description}
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </React.Fragment>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Select a variable from the dropdown to insert it at your cursor position
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateTemplate}
              disabled={isSaving || !formData.name || !formData.content}
            >
              {isSaving ? "Creating..." : "Create Template"}
            </Button>
          </DialogFooter>
        </ResponsiveDialogContent>
      </Dialog>

      {/* Edit Template Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <ResponsiveDialogContent size="2xl" className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit SMS Template</DialogTitle>
            <DialogDescription>
              Update your SMS template
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Template Name</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-category">Category</Label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TEMPLATE_CATEGORIES.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {isAgencyLeader && (
              <div className="space-y-2">
                <Label htmlFor="edit-scope">Template Scope</Label>
                <Select
                  value={formData.scope || "personal"}
                  onValueChange={(value: 'personal' | 'agency') => setFormData(prev => ({ ...prev, scope: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select scope" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="agency">
                      Agency-wide (All agents within the agency can use)
                    </SelectItem>
                    <SelectItem value="personal">
                      Personal (Only I can use)
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {formData.scope === 'agency'
                    ? "This template will be visible to all agents within your agency"
                    : "This template will only be visible to you"}
                </p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="edit-content">Template Content</Label>
              <Textarea
                ref={editContentTextareaRef}
                id="edit-content"
                className="min-h-[120px]"
                value={formData.content}
                onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
              />
              <p className="text-xs text-muted-foreground">
                Character count: {formData.content.length} / 160
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-insert-variable">Insert Variable</Label>
              <Select
                value=""
                onValueChange={(value) => {
                  if (value) {
                    insertPlaceholder(value, true);
                  }
                }}
              >
                <SelectTrigger id="edit-insert-variable">
                  <SelectValue placeholder="Select a variable to insert..." />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {Object.entries(
                    VARIABLE_PLACEHOLDERS.reduce((acc, placeholder) => {
                      if (!acc[placeholder.category]) {
                        acc[placeholder.category] = [];
                      }
                      acc[placeholder.category].push(placeholder);
                      return acc;
                    }, {} as Record<string, typeof VARIABLE_PLACEHOLDERS>)
                  ).map(([category, placeholders]) => (
                    <React.Fragment key={category}>
                      <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50">
                        {category}
                      </div>
                      {placeholders.map((placeholder) => (
                        <SelectItem key={placeholder.name} value={placeholder.name}>
                          <div className="flex items-center justify-between w-full gap-4">
                            <code className="font-mono text-xs font-semibold text-blue-600">
                              {placeholder.name}
                            </code>
                            <span className="text-xs text-muted-foreground">
                              {placeholder.description}
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </React.Fragment>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Select a variable from the dropdown to insert it at your cursor position
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleEditTemplate}
              disabled={isSaving || !formData.name || !formData.content}
            >
              {isSaving ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </ResponsiveDialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <ResponsiveAlertDialogContent size="md">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Template</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedTemplate?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteTemplate}
              disabled={isSaving}
              className="bg-red-600 hover:bg-red-700"
            >
              {isSaving ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </ResponsiveAlertDialogContent>
      </AlertDialog>
    </div>
  );
};
